/** These targets changed from Nudge Lite to a 70:30 split on this date. */
export const NUDGE_SPLIT_DATE = '2026-09-01';
export const VOLVO_NUDGE_LITE_START_DATE = '2025-11-11';
const VOLVO_TARGETS = new Set([
  'volvocars',
  'volvocars_ch_fr',
  'volvocars_ch_it',
]);
const SPLIT_TARGETS = new Set([...VOLVO_TARGETS, 'transa']);
// Components read the mode from templates as well as API branches.
let lastStartDate: string | undefined;
let lastTarget: string | null | undefined;
let lastHistoricalLite = false;

export function hasVolvoNudgePeriods(target: string | null | undefined): boolean {
  return VOLVO_TARGETS.has(target || '');
}

export function hasNudgeSplit(target: string | null | undefined): boolean {
  return SPLIT_TARGETS.has(target || '');
}

export function nudgeSplitDate(): Date {
  return new Date(2026, 8, 1);
}

export function nudgeLiteEndDate(): Date {
  return new Date(2026, 7, 31, 23, 59, 59, 999);
}

/** Inclusive local calendar bounds for the group split containing the selected day. */
export function getNudgePeriod(
  target: string | null | undefined,
  date: Date,
): { start: Date | null; end: Date | null } {
  if (!hasNudgeSplit(target)) return { start: null, end: null };
  if (date >= nudgeSplitDate()) return { start: nudgeSplitDate(), end: null };
  if (hasVolvoNudgePeriods(target)) {
    const liteStart = new Date(2025, 10, 11);
    return date < liteStart
      ? { start: null, end: new Date(2025, 10, 10, 23, 59, 59, 999) }
      : { start: liteStart, end: nudgeLiteEndDate() };
  }
  return { start: null, end: nudgeLiteEndDate() };
}

/** Stored dates are UTC instants with an @IANA-timezone suffix. */
export function resolveNudgeLite(
  target: string | null | undefined,
  startDate: string | null | undefined,
  configuredLite: boolean,
): boolean {
  if (!hasNudgeSplit(target) || !startDate) return configuredLite;
  if (startDate === lastStartDate && target === lastTarget) return lastHistoricalLite;

  const [iso, timeZone] = startDate.split('@');
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return configuredLite;

  try {
    const parts = new Intl.DateTimeFormat('en-US', {
      timeZone: timeZone || 'UTC',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    }).formatToParts(date);
    const part = (type: string) => parts.find((p) => p.type === type)?.value;
    const day = `${part('year')}-${part('month')}-${part('day')}`;
    const historicalLite = day < NUDGE_SPLIT_DATE &&
      (!hasVolvoNudgePeriods(target) || day >= VOLVO_NUDGE_LITE_START_DATE);
    lastStartDate = startDate;
    lastTarget = target;
    lastHistoricalLite = historicalLite;
    return historicalLite;
  } catch {
    return configuredLite;
  }
}

/** Preserve the selected start day; never include different group splits. */
export function constrainNudgeRange(startDate: Date, endDate: Date, target: string): Date {
  const periodEnd = getNudgePeriod(target, startDate).end;
  if (periodEnd && endDate > periodEnd) {
    return periodEnd;
  }
  return endDate;
}
